import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CheckCircle, AlertTriangle, XCircle, Download,
  RefreshCw, Loader2, ExternalLink, Shield, Scale, Eye,
  AlertCircle, ChevronRight, Globe, ShieldCheck, Pencil, History,
  ArrowLeft
} from 'lucide-react'
import { assessmentsApi, reportsApi } from '@/lib/api'
import { useAuthStore } from '@/store/auth'
import { useEntitlements } from '@/hooks/use-entitlements'
import { useActiveDelegation } from '@/hooks/use-delegation'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Callout } from '@/components/ui/callout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { AssessmentRadarChart } from '@/components/RadarChart'
import { PipelineTimeline } from '@/components/PipelineTimeline'
import { ScoreGauge } from '@/components/ScoreGauge'
import { cn, verdictBadgeColor, verdictLabel, scoreBarColor, dimensionLabel, formatDate, roleLabel } from '@/lib/utils'
import type { Assessment, DpiaTemplate, Verdict } from '@/types'

// ── Verdict Banner ────────────────────────────────────────────────────────────

function VerdictBanner({ verdict, score, rationale }: {
  verdict: Verdict; score: number; rationale?: string
}) {
  const { t } = useTranslation()
  const icons: Record<Verdict, JSX.Element> = {
    VERDE: <CheckCircle className="w-8 h-8 text-verde" />,
    GIALLO: <AlertTriangle className="w-8 h-8 text-giallo" />,
    ROSSO: <XCircle className="w-8 h-8 text-rosso" />,
    VIETATO: <XCircle className="w-8 h-8 text-rosso-dark" />,
  }
  const bgColors: Record<Verdict, string> = {
    VERDE: 'bg-verde-light border-verde/30',
    GIALLO: 'bg-giallo-light border-giallo/30',
    ROSSO: 'bg-rosso-light border-rosso/30',
    VIETATO: 'bg-rosso-light border-rosso-dark/60',
  }
  const textColors: Record<Verdict, string> = {
    VERDE: 'text-verde-dark',
    GIALLO: 'text-giallo-dark',
    ROSSO: 'text-rosso-dark',
    VIETATO: 'text-rosso-dark',
  }

  return (
    <div className={cn('p-5 rounded-xl border-2 animate-fade-up', bgColors[verdict])}>
      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
        <ScoreGauge score={score} verdict={verdict} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="flex-shrink-0">{icons[verdict]}</span>
            <h2 className={cn('text-xl font-bold', textColors[verdict])}>
              {t(`assessment.verdictLabels.${verdict}` as any)}
            </h2>
          </div>
          <p className={cn('text-sm mt-1', textColors[verdict])}>
            {t(`assessment.verdictDesc.${verdict}` as any)}
          </p>
          {rationale && (
            // Lo sfondo del banner è un colore chiaro fisso anche in dark
            // mode: il testo deve restare scuro (i token *-dark del verdetto),
            // non seguire --foreground che in dark diventa bianco.
            <p className={cn('text-sm mt-2 opacity-90', textColors[verdict])}>{rationale}</p>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Run History (archived previous outcomes, read-only) ──────────────────────

function RunHistory({ assessmentId }: { assessmentId: string }) {
  const { t } = useTranslation()
  const { data: runs } = useQuery({
    queryKey: ['assessment-runs', assessmentId],
    queryFn: () => assessmentsApi.runs(assessmentId),
  })
  if (!runs || runs.length === 0) return null

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <History className="w-5 h-5 text-mim-blue" />
          {t('assessment.runs.title')}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t('assessment.runs.subtitle')}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {runs.map((run) => (
          <div key={run.id} className="border border-border rounded-lg p-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="text-xs font-mono">v{run.version}</Badge>
              {run.verdict && (
                <Badge className={cn('text-xs', verdictBadgeColor(run.verdict))}>
                  {verdictLabel(run.verdict)}
                </Badge>
              )}
              {run.overall_score != null && (
                <span className="text-xs text-muted-foreground">
                  {t('assessment.runs.score')}:{' '}
                  <b>{Number(run.overall_score).toFixed(1)}/5</b>
                </span>
              )}
              <span className="text-xs text-muted-foreground ml-auto">
                {t('assessment.runs.archivedAt')} {formatDate(run.archived_at)}
              </span>
            </div>
            <div className="mt-2 flex items-center gap-x-2 gap-y-1 flex-wrap text-xs text-muted-foreground">
              {run.review_status === 'APPROVED' || run.review_status === 'OVERRIDDEN' ? (
                <span className="inline-flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-verde" />
                  {run.review_status === 'OVERRIDDEN'
                    ? t('assessment.runs.overridden')
                    : t('assessment.runs.authorized')}
                  {run.reviewer && <> — {run.reviewer.name}</>}
                </span>
              ) : run.dpo_validated_at ? (
                <span className="inline-flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-mim-blue" />
                  {t('assessment.runs.dpoValidated')}
                  {run.dpo_validator && <> — {run.dpo_validator.name}</>}
                </span>
              ) : (
                <span>{t('assessment.runs.noValidation')}</span>
              )}
              {run.original_verdict && (
                <span>
                  · {t('assessment.runs.originalVerdict')}: {verdictLabel(run.original_verdict)}
                </span>
              )}
              {run.engine_version && <span className="font-mono">· {run.engine_version}</span>}
            </div>
            {(run.dpo_notes || run.review_notes) && (
              <div className="mt-2 space-y-1">
                {run.dpo_notes && (
                  <p className="text-xs text-muted-foreground">
                    <b>{t('assessment.runs.notesDpo')}:</b> {run.dpo_notes}
                  </p>
                )}
                {run.review_notes && (
                  <p className="text-xs text-muted-foreground">
                    <b>{t('assessment.runs.notesReviewer')}:</b> {run.review_notes}
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

// ── Score Dimension Row ────────────────────────────────────────────────────────

function DimensionRow({ ds }: { ds: Assessment['dimension_scores'][0] }) {
  const [expanded, setExpanded] = useState(false)
  const score = Number(ds.score)
  const pct = (score / 5) * 100

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-4 p-4 hover:bg-muted/40 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-sm font-semibold">{dimensionLabel(ds.dimension)}</span>
            <Badge
              className={cn(
                'text-xs',
                score >= 4 ? 'bg-verde text-white' : score >= 2.5 ? 'bg-giallo-dark text-white' : 'bg-rosso text-white'
              )}
            >
              {score.toFixed(1)}/5
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-muted rounded-full h-2">
              <div
                className={cn('h-2 rounded-full transition-all', scoreBarColor(score))}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground">{Math.round(pct)}%</span>
          </div>
        </div>
        <ChevronRight className={cn('w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform', expanded && 'rotate-90')} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-border bg-muted/20">
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{ds.rationale}</p>
          {ds.normative_references.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                Riferimenti normativi
              </p>
              <div className="space-y-1">
                {ds.normative_references.map((ref, i) => (
                  <div key={i} className="text-xs flex items-center gap-2 text-muted-foreground">
                    <Scale className="w-3 h-3 flex-shrink-0" />
                    <span>
                      <strong>{ref.document}</strong>{' '}
                      {ref.article || ref.paragraph}{' '}
                      {ref.description && `– ${ref.description}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Human Review (Art. 14 AI Act) ──────────────────────────────────────────────

const REVIEWABLE_VERDICTS: Verdict[] = ['VERDE', 'GIALLO', 'ROSSO', 'VIETATO']

function ReviewSection({ assessment }: { assessment: Assessment }) {
  const { t, i18n } = useTranslation()
  const { user } = useAuthStore()
  const { data: entitlements } = useEntitlements()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [overrideOpen, setOverrideOpen] = useState(false)
  const [validateOpen, setValidateOpen] = useState(false)
  const [validateNotes, setValidateNotes] = useState('')
  const [newVerdict, setNewVerdict] = useState<string>('')
  const [notes, setNotes] = useState('')

  const roleQualifies = !!user && ['DPO', 'DIRIGENTE'].includes(user.role)
  // Il diritto segue la scuola della valutazione (il DPO cross-tenant non ha
  // un piano proprio); fallback sugli entitlements per le valutazioni solo-utente
  const reviewAllowed = assessment.school_plan
    ? assessment.school_plan !== 'FREE'
    : !!entitlements?.can_request_review
  const delegation = useActiveDelegation()
  const isDpo = user?.role === 'DPO'
  // P7: the active verdict-scope delegate acts at step 2 like the Dirigente
  const isDirigente = user?.role === 'DIRIGENTE'
    || (!!delegation?.scope_verdetti && delegation.school_id === assessment.school_id)
  const dpoValidated = !!assessment.dpo_validated_at
  // Two-step chain: DPO validates first, then the Dirigente authorizes.
  const canActNow =
    reviewAllowed && ((!dpoValidated && isDpo) || (dpoValidated && isDirigente))

  const { mutate: review, isPending } = useMutation({
    mutationFn: (data: { decision: 'APPROVE' | 'OVERRIDE'; verdict?: string; notes?: string }) =>
      assessmentsApi.review(assessment.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessment', assessment.id] })
      toast({ title: t('assessment.review.reviewSuccess') })
      setOverrideOpen(false)
      setValidateOpen(false)
    },
    onError: () => toast({ title: t('assessment.review.reviewError'), variant: 'destructive' }),
  })

  if (!assessment.review_status) return null

  if (assessment.review_status === 'PENDING') {
    return (
      <div className="p-4 bg-giallo-light border border-giallo/40 rounded-lg">
        <div className="flex items-start gap-3">
          <Eye className="w-5 h-5 text-giallo-dark flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-giallo-dark">
              {t(dpoValidated ? 'assessment.review.authStageTitle' : 'assessment.review.dpoStageTitle')}
            </p>
            <p className="text-xs text-giallo-dark mt-1">
              {t(dpoValidated ? 'assessment.review.authStageBody' : 'assessment.review.dpoStageBody')}
            </p>
            {dpoValidated && (
              <p className="text-xs text-giallo-dark mt-2">
                <span className="font-semibold">{t('assessment.review.dpoValidatedBy')}:</span>{' '}
                {assessment.dpo_validator?.name}
                {assessment.dpo_validated_at && ` · ${formatDate(assessment.dpo_validated_at, i18n.language)}`}
                {assessment.dpo_notes && <span className="block italic mt-0.5">{assessment.dpo_notes}</span>}
              </p>
            )}
            {canActNow && (
              <div className="flex items-center gap-2 mt-3">
                <Button
                  size="sm"
                  disabled={isPending}
                  onClick={() => setValidateOpen(true)}
                  className="bg-verde hover:bg-verde-dark text-white"
                >
                  {isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ShieldCheck className="w-4 h-4 mr-2" />
                  )}
                  {t(dpoValidated ? 'assessment.review.authorize' : 'assessment.review.validate')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isPending}
                  onClick={() => setOverrideOpen(true)}
                  className="border-giallo text-giallo-dark hover:bg-giallo-light"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  {t(dpoValidated ? 'assessment.review.authorizeOverride' : 'assessment.review.validateOverride')}
                </Button>
              </div>
            )}
            {roleQualifies && reviewAllowed && !canActNow && (
              <p className="text-xs text-giallo-dark mt-3 italic">
                {t(dpoValidated ? 'assessment.review.waitDir' : 'assessment.review.waitDpo')}
              </p>
            )}
            {roleQualifies && !reviewAllowed && (
              <p className="text-xs text-giallo-dark mt-3 italic">
                L'inoltro per la validazione umana è disponibile con il piano d'istituto.
              </p>
            )}
          </div>
        </div>

        {/* Conferma con note facoltative: la valutazione del DPO (o del
            Dirigente) viene comunicata al passo successivo della catena */}
        <Dialog open={validateOpen} onOpenChange={setValidateOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t(dpoValidated ? 'assessment.review.authorize' : 'assessment.review.validate')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t(dpoValidated ? 'assessment.review.confirmAuthorize' : 'assessment.review.confirmValidate')}
              </p>
              <div>
                <Label>Note (facoltative)</Label>
                <Textarea
                  value={validateNotes}
                  onChange={(e) => setValidateNotes(e.target.value)}
                  className="mt-1.5"
                  rows={3}
                  placeholder={dpoValidated
                    ? 'Osservazioni del Dirigente sulla decisione…'
                    : 'La valutazione del DPO, visibile al Dirigente nel passo successivo…'}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setValidateOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                disabled={isPending}
                onClick={() => review({ decision: 'APPROVE', notes: validateNotes.trim() || undefined })}
                className="bg-verde hover:bg-verde-dark text-white"
              >
                {isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t(dpoValidated ? 'assessment.review.authorize' : 'assessment.review.validate')
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('assessment.review.overrideTitle')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t('assessment.review.overrideDesc')}
              </p>
              <div>
                <Label>{t('assessment.review.newVerdict')} *</Label>
                <Select value={newVerdict} onValueChange={setNewVerdict}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="…" />
                  </SelectTrigger>
                  <SelectContent>
                    {REVIEWABLE_VERDICTS.map((v) => (
                      <SelectItem key={v} value={v}>
                        {verdictLabel(v, i18n.language)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('assessment.review.notes')} *</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="mt-1.5"
                  rows={3}
                  placeholder={t('assessment.review.notesPlaceholder')}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOverrideOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                disabled={!newVerdict || !notes.trim() || isPending}
                onClick={() => review({ decision: 'OVERRIDE', verdict: newVerdict, notes })}
                className="bg-mim-blue text-white"
              >
                {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    )
  }

  // APPROVED / OVERRIDDEN: due riquadri distinti, uno per passo della catena
  // — il DPO valida sempre per primo, il Dirigente autorizza (o modifica) poi
  const overridden = assessment.review_status === 'OVERRIDDEN'
  return (
    <div className="space-y-2">
      {assessment.dpo_validated_at && (
        <div className="flex items-start gap-3 p-3 rounded-lg border text-xs bg-mim-lightblue border-mim-blue/30 text-mim-blue dark:bg-primary/10">
          <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold">{t('assessment.review.dpoValidatedBy')}</span>{' '}
            {assessment.dpo_validator?.name}
            {` · ${formatDate(assessment.dpo_validated_at, i18n.language)}`}
            {assessment.dpo_notes && (
              <span className="block mt-1 italic">{assessment.dpo_notes}</span>
            )}
          </div>
        </div>
      )}
      <div className={cn(
        'flex items-start gap-3 p-3 rounded-lg border text-xs',
        overridden
          ? 'bg-mim-lightblue border-mim-blue/30 text-mim-blue dark:bg-primary/10'
          : 'bg-verde-light border-verde/30 text-verde-dark'
      )}>
        <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          <span className="font-semibold">
            {t(overridden ? 'assessment.review.overriddenBy' : 'assessment.review.approvedBy')}
          </span>{' '}
          {assessment.reviewer?.name} ({roleLabel(assessment.reviewer?.role || '')})
          {assessment.reviewed_at && ` · ${formatDate(assessment.reviewed_at, i18n.language)}`}
          {overridden && assessment.original_verdict && (
            <span className="block mt-1">
              {t('assessment.review.originalVerdict')}:{' '}
              <strong>{verdictLabel(assessment.original_verdict, i18n.language)}</strong>
            </span>
          )}
          {assessment.review_notes && (
            <span className="block mt-1 italic">{assessment.review_notes}</span>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Revisione DPIA (GDPR Art. 35) ──────────────────────────────────────────────
// La DPIA generata automaticamente è preliminare per progetto: il DPO la
// completa da qui prima di comunicare la sua validazione al Dirigente.

const DPIA_TEXT_FIELDS: Array<{ key: keyof DpiaTemplate; label: string }> = [
  { key: 'titolare_trattamento', label: 'Titolare del trattamento' },
  { key: 'responsabile_trattamento', label: 'Responsabile del trattamento' },
  { key: 'finalita_trattamento', label: 'Finalità del trattamento' },
  { key: 'base_giuridica', label: 'Base giuridica' },
  { key: 'conservazione_dati', label: 'Periodo di conservazione' },
  { key: 'trasferimento_extra_ue', label: 'Trasferimento extra-UE' },
]
const DPIA_LIST_FIELDS: Array<{ key: keyof DpiaTemplate; label: string }> = [
  { key: 'categorie_dati', label: 'Categorie di dati (una per riga)' },
  { key: 'diritti_interessati', label: 'Diritti degli interessati (uno per riga)' },
  { key: 'misure_tecniche_organizzative', label: 'Misure tecniche e organizzative (una per riga)' },
]

function DpiaEditDialog({
  assessment, open, onClose,
}: { assessment: Assessment; open: boolean; onClose: () => void }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [form, setForm] = useState<Record<string, string>>({})

  // Il form riparte sempre dal contenuto corrente (generato o già revisionato)
  useEffect(() => {
    if (!open || !assessment.dpia_template) return
    const tpl = assessment.dpia_template as unknown as Record<string, unknown>
    const init: Record<string, string> = {}
    DPIA_TEXT_FIELDS.forEach(({ key }) => { init[key] = String(tpl[key] ?? '') })
    DPIA_LIST_FIELDS.forEach(({ key }) => {
      init[key] = (((tpl[key] as string[]) ?? [])).join('\n')
    })
    setForm(init)
  }, [open, assessment.dpia_template])

  const { mutate: save, isPending } = useMutation({
    mutationFn: () => {
      const payload = {} as Record<string, unknown>
      DPIA_TEXT_FIELDS.forEach(({ key }) => { payload[key] = form[key] ?? '' })
      DPIA_LIST_FIELDS.forEach(({ key }) => {
        payload[key] = (form[key] ?? '').split('\n').map((s) => s.trim()).filter(Boolean)
      })
      return assessmentsApi.updateDpia(assessment.id, payload as Partial<DpiaTemplate>)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessment', assessment.id] })
      toast({
        title: 'DPIA aggiornata',
        description: 'Il PDF del report viene rigenerato con la revisione.',
      })
      onClose()
    },
    onError: () =>
      toast({ title: 'Errore nel salvataggio della DPIA', variant: 'destructive' }),
  })

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Revisione DPIA del DPO</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          I campi sono precompilati con la DPIA generata automaticamente:
          integra o correggi il testo, poi salva. La revisione entra nel PDF
          del report e resta tracciata nel registro attività.
        </p>
        <div className="space-y-4">
          {DPIA_TEXT_FIELDS.map(({ key, label }) => (
            <div key={key}>
              <Label>{label}</Label>
              <Textarea
                value={form[key] ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                className="mt-1.5"
                rows={2}
              />
            </div>
          ))}
          {DPIA_LIST_FIELDS.map(({ key, label }) => (
            <div key={key}>
              <Label>{label}</Label>
              <Textarea
                value={form[key] ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                className="mt-1.5"
                rows={4}
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button
            disabled={isPending}
            onClick={() => save()}
            className="bg-mim-blue text-white"
          >
            {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salva revisione'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function AssessmentResults() {
  const { id } = useParams<{ id: string }>()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { data: entitlements } = useEntitlements()
  const { user } = useAuthStore()

  const { data: assessment, isLoading, refetch } = useQuery({
    queryKey: ['assessment', id],
    queryFn: () => assessmentsApi.get(id!),
    refetchInterval: (query) => {
      const data = query.state?.data as Assessment | undefined;
      if (!data || !data.status) return 2000;
      return ['SEARCHING', 'ANALYZING'].includes(data.status) ? 2000 : false;
    },
  })

  const { mutate: rerun, isPending: rerunning } = useMutation({
    mutationFn: () => assessmentsApi.rerun(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessment', id] })
      toast({ title: 'Ri-analisi avviata' })
    },
  })

  const isInProgress = assessment && ['SEARCHING', 'ANALYZING'].includes(assessment.status)
  // Il rerun è del proprietario (il backend lo impone già): chi consulta la
  // valutazione di altri — DPO/Dirigente dalla catena — non vede il pulsante
  const isOwner = !!user && assessment?.user_id === user.id
  // Richiedenti (docente/segreteria): niente stampa né rianalisi — decisione
  // del Dirigente. La stampa resta a DPO/Dirigente/ISPETTORE/admin.
  const isRequester = user?.role === 'DOCENTE' || user?.role === 'SEGRETERIA'
  const canRerun = isOwner && !isRequester
  // Il PDF formale segue il piano della scuola della valutazione (il DPO che
  // esamina dal Cruscotto non ha un piano proprio); fallback sugli
  // entitlements del visitatore per le valutazioni senza scuola
  const canPdf = assessment?.school_plan
    ? assessment.school_plan !== 'FREE'
    : !!entitlements?.can_download_pdf
  const [dpiaOpen, setDpiaOpen] = useState(false)
  // La revisione DPIA è del DPO, finché la catena è aperta (l'autorizzazione
  // del Dirigente àncora il documento via SHA-256)
  const canEditDpia =
    user?.role === 'DPO' && assessment?.review_status === 'PENDING'

  if (isLoading) {
    return (
      <div className="p-6 max-w-4xl mx-auto space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    )
  }

  if (!assessment) return null

  // Ritorno alla superficie di provenienza (Cruscotto DPO, Registro,
  // Cronologia, coda di revisione…): visibile solo se c'è davvero una voce
  // precedente nella history della SPA (idx di React Router), così non
  // compare sui deep-link aperti direttamente.
  const canGoBack = (window.history.state?.idx ?? 0) > 0

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {canGoBack && (
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-xs text-mim-blue dark:text-primary hover:underline underline-offset-2 -mb-2"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {t('common.back')}
        </button>
      )}
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="it-page-title">
          <p className="text-[11px] font-bold uppercase tracking-wider text-mim-blue dark:text-primary mb-1">
            Valutazione del rischio
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold">{assessment.app.name}</h1>
            <Badge variant="outline">{assessment.app.category}</Badge>
            <Badge variant="outline" className="text-xs">
              {t('assessment.version')} {assessment.version}
            </Badge>
          </div>
          <p className="text-muted-foreground text-sm mt-1">
            {assessment.app.vendor && `${assessment.app.vendor} · `}
            {formatDate(assessment.created_at, i18n.language)}
            {assessment.valid_until && !assessment.needs_revalidation && (
              <> · {t('assessment.revalidation.validUntil', {
                date: formatDate(assessment.valid_until, i18n.language),
              })}</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {assessment.status === 'COMPLETED' && (
            <>
              {!isRequester && canPdf && (
                <Button variant="outline" size="sm" asChild>
                  <a href={reportsApi.downloadUrl(assessment.id)} download>
                    <Download className="w-4 h-4 mr-2" />
                    {t('assessment.downloadReport')}
                  </a>
                </Button>
              )}
              {canRerun && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => rerun()}
                  disabled={rerunning}
                  aria-label="Ri-analizza la valutazione"
                  title="Ri-analizza la valutazione"
                >
                  {rerunning ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* In-progress pipeline timeline */}
      {isInProgress && <PipelineTimeline status={assessment.status} />}

      {/* Failed state */}
      {assessment.status === 'FAILED' && (
        <Card className="border-rosso/40">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-rosso-dark">
              <AlertCircle className="w-5 h-5" />
              <div>
                <p className="font-medium">Analisi non completata – revisione manuale richiesta</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {assessment.verdict_rationale ||
                    "L'analisi automatica non è disponibile. Non utilizzare l'applicazione prima di una valutazione manuale del DPO."}
                </p>
              </div>
              {canRerun && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => rerun()}
                  className="ml-auto border-rosso text-rosso-dark hover:bg-rosso-light"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Riprova
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {assessment.status === 'COMPLETED' && assessment.verdict && (
        <>
          {/* Periodic review (DM 166/2025 §10.1): expired or engine-outdated */}
          {assessment.needs_revalidation && (
            <Callout
              variant="warning"
              title={t('assessment.revalidation.title')}
              icon={<RefreshCw className="w-4 h-4" />}
            >
              {assessment.valid_until && new Date(assessment.valid_until) < new Date()
                ? t('assessment.revalidation.expired', {
                    date: formatDate(assessment.valid_until, i18n.language),
                  })
                : t('assessment.revalidation.engineOutdated')}
            </Callout>
          )}

          {/* DPIA Warning */}
          {assessment.requires_dpia && (
            <Callout variant="danger" title="DPIA Richiesta" icon={<Shield className="w-4 h-4" />}>
              È necessaria una Data Protection Impact Assessment prima dell'uso.
            </Callout>
          )}

          {/* FRIA Warning */}
          {assessment.requires_fria && (
            <Callout variant="note" title={t('assessment.friaRequired')} icon={<Scale className="w-4 h-4" />}>
              Sistema ad alto rischio: valutazione d'impatto sui diritti fondamentali
              obbligatoria per gli organismi pubblici (Art. 27 Reg. UE 2024/1689).
            </Callout>
          )}

          {!assessment.web_search_available && (
            <Callout variant="warning" title="Ricerca web non disponibile" icon={<Globe className="w-4 h-4" />}>
              {t('assessment.webSearchUnavailable')}
            </Callout>
          )}

          {/* Human review (Art. 14 AI Act) */}
          <ReviewSection assessment={assessment} />

          {/* Verdict */}
          <VerdictBanner
            verdict={assessment.verdict}
            score={assessment.overall_score!}
            rationale={assessment.verdict_rationale}
          />

          {/* Tabs */}
          <Tabs defaultValue="overview">
            <TabsList className="flex flex-wrap h-auto w-full">
              <TabsTrigger value="overview">{t('assessment.tabs.overview')}</TabsTrigger>
              <TabsTrigger value="dimensions">{t('assessment.tabs.dimensions')}</TabsTrigger>
              <TabsTrigger value="recommendations">{t('assessment.tabs.recommendations')}</TabsTrigger>
              <TabsTrigger value="mitigation">{t('assessment.tabs.mitigation')}</TabsTrigger>
              <TabsTrigger value="sources">{t('assessment.tabs.sources')}</TabsTrigger>
              {assessment.requires_dpia && (
                <TabsTrigger value="dpia">{t('assessment.tabs.dpia')}</TabsTrigger>
              )}
              {assessment.requires_fria && assessment.fria_template && (
                <TabsTrigger value="fria">{t('assessment.tabs.fria')}</TabsTrigger>
              )}
            </TabsList>

            {/* Overview */}
            <TabsContent value="overview" className="mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="it-card-accent">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Grafico Radar</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <AssessmentRadarChart scores={assessment.dimension_scores} />
                  </CardContent>
                </Card>
                <Card className="it-card-accent">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Punteggi per Dimensione</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {assessment.dimension_scores.map((ds) => {
                      const score = Number(ds.score)
                      return (
                        <div key={ds.id}>
                          <div className="flex justify-between text-sm mb-1">
                            <span>{dimensionLabel(ds.dimension)}</span>
                            <span className="font-bold">{score.toFixed(1)}/5</span>
                          </div>
                          <div className="bg-muted rounded-full h-2">
                            <div
                              className={cn('h-2 rounded-full', scoreBarColor(score))}
                              style={{ width: `${(score / 5) * 100}%` }}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Dimensions Detail */}
            <TabsContent value="dimensions" className="mt-4 space-y-3">
              {assessment.dimension_scores.map((ds) => (
                <DimensionRow key={ds.id} ds={ds} />
              ))}
            </TabsContent>

            {/* Recommendations */}
            <TabsContent value="recommendations" className="mt-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{t('assessment.recommendations')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-3">
                    {(assessment.recommendations || []).map((rec, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-mim-lightblue text-mim-blue font-bold text-xs flex items-center justify-center">
                          {i + 1}
                        </span>
                        <p className="text-sm text-muted-foreground leading-relaxed">{rec}</p>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Mitigation Plan */}
            <TabsContent value="mitigation" className="mt-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{t('assessment.mitigationPlan')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 pr-4 font-semibold text-muted-foreground text-xs uppercase">
                            {t('assessment.mitigationRisk')}
                          </th>
                          <th className="text-left py-2 pr-4 font-semibold text-muted-foreground text-xs uppercase">
                            {t('assessment.mitigationMeasure')}
                          </th>
                          <th className="text-left py-2 pr-4 font-semibold text-muted-foreground text-xs uppercase">
                            {t('assessment.mitigationOwner')}
                          </th>
                          <th className="text-left py-2 pr-4 font-semibold text-muted-foreground text-xs uppercase">
                            {t('assessment.mitigationDeadline')}
                          </th>
                          <th className="text-left py-2 font-semibold text-muted-foreground text-xs uppercase">
                            {t('assessment.mitigationPriority')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(assessment.mitigation_plan || []).map((item, i) => (
                          <tr key={i} className="border-b border-border/50 hover:bg-muted/30">
                            <td className="py-3 pr-4 text-xs">{item.rischio}</td>
                            <td className="py-3 pr-4 text-xs">{item.misura}</td>
                            <td className="py-3 pr-4 text-xs font-medium">{item.responsabile}</td>
                            <td className="py-3 pr-4 text-xs">{item.scadenza}</td>
                            <td className="py-3">
                              <Badge className={cn(
                                'text-xs',
                                item.priorita === 'Alta' && 'bg-rosso text-white',
                                item.priorita === 'Media' && 'bg-giallo-dark text-white',
                                item.priorita === 'Bassa' && 'bg-verde text-white',
                              )}>
                                {item.priorita}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Web Sources */}
            <TabsContent value="sources" className="mt-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{t('assessment.webSources')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {assessment.web_research_results.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('assessment.webSearchUnavailable')}</p>
                  ) : (
                    <div className="space-y-3">
                      {assessment.web_research_results.map((r) => (
                        <div key={r.id} className="border border-border rounded-lg p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="text-xs">{r.source_type}</Badge>
                                {r.is_verified && (
                                  <Badge className="bg-verde text-white text-xs">Verificata</Badge>
                                )}
                              </div>
                              {r.title && <p className="text-sm font-medium">{r.title}</p>}
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                {r.content_summary}
                              </p>
                            </div>
                            <a
                              href={r.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-shrink-0"
                            >
                              <ExternalLink className="w-4 h-4 text-muted-foreground hover:text-mim-blue" />
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* DPIA */}
            {assessment.requires_dpia && assessment.dpia_template && (
              <TabsContent value="dpia" className="mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Shield className="w-5 h-5 text-rosso" />
                      {t('assessment.dpiaSectionTitle')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Callout variant="warning" title="Da completare">
                      Questo template DPIA è generato automaticamente e deve essere completato
                      e revisionato dal DPO dell'istituto prima di qualsiasi trattamento.
                    </Callout>
                    {canEditDpia && assessment.dpia_template && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDpiaOpen(true)}
                        className="border-mim-blue text-mim-blue hover:bg-mim-lightblue"
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Revisiona DPIA
                      </Button>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(assessment.dpia_template)
                        .filter(([k]) => !['misure_tecniche_organizzative', 'diritti_interessati', 'categorie_dati'].includes(k))
                        .map(([key, value]) => (
                          <div key={key}>
                            <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">
                              {key.replace(/_/g, ' ')}
                            </p>
                            <p className="text-sm">{String(value)}</p>
                          </div>
                        ))}
                    </div>
                    {assessment.dpia_template.misure_tecniche_organizzative && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                          Misure Tecniche e Organizzative
                        </p>
                        <ul className="space-y-1">
                          {(assessment.dpia_template.misure_tecniche_organizzative as string[]).map((m, i) => (
                            <li key={i} className="text-sm flex items-center gap-2">
                              <CheckCircle className="w-3 h-3 text-verde flex-shrink-0" />
                              {m}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* FRIA (Art. 27 AI Act) */}
            {assessment.requires_fria && assessment.fria_template && (
              <TabsContent value="fria" className="mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Scale className="w-5 h-5 text-mim-blue" />
                      {t('assessment.friaSectionTitle')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Callout variant="warning" title="Nota">
                      {t('assessment.friaNote')}
                    </Callout>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">
                          Processi interessati
                        </p>
                        <p className="text-sm">{assessment.fria_template.processi_interessati}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">
                          Periodo e frequenza d'uso
                        </p>
                        <p className="text-sm">{assessment.fria_template.periodo_frequenza_uso}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">
                          Procedura reclami
                        </p>
                        <p className="text-sm">{assessment.fria_template.procedura_reclami}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">
                          Periodicità riesame
                        </p>
                        <p className="text-sm">{assessment.fria_template.periodicita_riesame}</p>
                      </div>
                    </div>

                    {assessment.fria_template.categorie_interessati?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                          Categorie di interessati
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {assessment.fria_template.categorie_interessati.map((c, i) => (
                            <Badge key={i} variant="outline">{c}</Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {assessment.fria_template.rischi_diritti_fondamentali?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                          {t('assessment.friaRights')}
                        </p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-border">
                                <th className="text-left py-2 pr-4 font-semibold text-muted-foreground text-xs uppercase">
                                  {t('assessment.friaRight')}
                                </th>
                                <th className="text-left py-2 pr-4 font-semibold text-muted-foreground text-xs uppercase">
                                  {t('assessment.friaRisk')}
                                </th>
                                <th className="text-left py-2 font-semibold text-muted-foreground text-xs uppercase">
                                  {t('assessment.friaProbability')}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {assessment.fria_template.rischi_diritti_fondamentali.map((r, i) => (
                                <tr key={i} className="border-b border-border/50">
                                  <td className="py-2 pr-4 text-xs font-medium">{r.diritto}</td>
                                  <td className="py-2 pr-4 text-xs">{r.rischio}</td>
                                  <td className="py-2 text-xs">{r.probabilita}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {[
                      ['friaOversight', assessment.fria_template.misure_supervisione_umana],
                      ['friaGovernance', assessment.fria_template.misure_governance],
                    ].map(([key, items]) => (
                      Array.isArray(items) && items.length > 0 && (
                        <div key={key as string}>
                          <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                            {t(`assessment.${key}` as any)}
                          </p>
                          <ul className="space-y-1">
                            {(items as string[]).map((m, i) => (
                              <li key={i} className="text-sm flex items-center gap-2">
                                <CheckCircle className="w-3 h-3 text-verde flex-shrink-0" />
                                {m}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>

          {/* Archived previous outcomes (D8) */}
          <RunHistory assessmentId={assessment.id} />
        </>
      )}

      {/* Revisione DPIA del DPO */}
      {canEditDpia && assessment.dpia_template && (
        <DpiaEditDialog
          assessment={assessment}
          open={dpiaOpen}
          onClose={() => setDpiaOpen(false)}
        />
      )}
    </div>
  )
}
